import {Inject, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, finalize, map, Observable, of, shareReplay} from "rxjs";
import {UserJwt} from "../models/admin_owner/user/user-jwt";
import {AppRoutes} from "../enums/routes";
import {
  BracketedAccount,
  ExternalIdentityInfo,
  UserAuthenticators
} from "../models/admin_owner/user/external-identity-info";
import {ActivatedRoute, Router} from "@angular/router";
import {HttpClient} from "@angular/common/http";
import {GeneralHttpService} from "../http-requests/general-http.service";
import {Roles} from "./x-enums/roles";
import {CookieService} from "../_services/cookie.service";
import {CookieType} from "../x-models/x-enums/cookie-type";
import {UserSelectorService} from "../_services/user-selector.service";
import {UrlHelper} from "../helpers/url-helper";
import {ContractType} from "../x-components/navbar-base/_models/contract-type";
import {MetadataType} from "../_models/_auth/_enums/metadata-type";
import {
  ContextOptions,
  ContextRecoveryNotice,
  ContextView,
  EnterContextRequest,
  SelectableContext,
  SelectableContextKind
} from "./context/context.models";
import {AccountSecurity} from "./two-factor/two-factor.models";
import {ContactGate} from "./contact/contact-link.models";
import {InvitationEmailConfirmationChallenge, LegacyClaimResult} from "./migration/migration.models";
import {InvitationRedeemLookup} from "../_modules/company/access/grant.models";

/**
 * The external sign-in providers the UI offers. Declared once here because the same union is needed by every
 * caller that starts a flow (login, link, claim, redeem) — a local re-declaration is how one of them ends up
 * accepting a provider the routing does not handle.
 *
 * Must live ABOVE the decorator: between `@Injectable` and its class it is `TS1206: Decorators are not valid here`.
 */
export type LoginProvider = 'apple' | 'google' | 'microsoft';

/**
 * The two things a native invitation redemption may carry beyond the address and the password.
 *
 * Declared here, above the decorator, for the same reason {@link LoginProvider} is: between `@Injectable` and its
 * class TypeScript reports `TS1206: Decorators are not valid here`.
 */
export interface InvitationRedeemOptions {
  /** A display name for the account picker. The address is used when it is absent. */
  displayName?: string;
  /**
   * The six-digit mailbox confirmation code, for an **enforced** invitation that was handed over rather than
   * mailed (#580).
   *
   * Ignored by the server unless it is actually needed, so it is always safe to send. When it IS needed and
   * absent, the answer is `E_GRANT_INVITE_CONFIRMATION_REQUIRED` rather than an address mismatch, which is what
   * lets the form show a code field instead of a dead end.
   */
  emailConfirmationCode?: string;
}

// One decorator, not two — the duplicate here predates #279 and was harmless only because Angular took the
// first and ignored the rest.
@Injectable({providedIn: 'root'})
export class AuthenticationService {
  private userSubject: BehaviorSubject<UserJwt | null>;
  public user: Observable<UserJwt | null>;

  constructor(private router: Router,
              private http: HttpClient,
              @Inject("BASE_URL") private baseUrl: string,
              private generalHttpService: GeneralHttpService,
              private cookieService: CookieService,
              private userSelectorService: UserSelectorService,
              private route: ActivatedRoute) {
    this.userSubject = new BehaviorSubject((JSON.parse(localStorage.getItem('user')!)));
    this.user = this.userSubject.asObservable();
  }

  public get userValue() {
    return this.userSubject.value;
  }

  /**
   * The **legacy** sign-in: an employee by e-mail + host alias or `benutzername@firmen-kürzel`, or a management
   * user by name (#279).
   *
   * Deliberately NOT the default form any more. All three spellings go into one field here and the server picks a
   * branch from the shape of the string, which is exactly the silent disambiguation the split into two forms
   * removes — see {@link loginWithPassword}, which resolves a disjoint population and guesses nothing.
   */
  login(username: string, password: string) {
    return this.http.post<any>(this.baseUrl + "/auth/user/Login", {
      "Name": username,
      "Password": password
    }, {
      withCredentials: true
    }).pipe(map(user => {
      this.cookieService.setLocalStorage(CookieType.Required, 'user', JSON.stringify(user));
      this.userSubject.next(user);

      this.userSelectorService.add(user);

      return user;
    }));
  }

  // ----- native (e-mail + password) accounts (#279) -----

  /**
   * Signs in with an e-mail address and a password — the primary form.
   *
   * Resolves **only** native accounts: an employee or a legacy management user is not reachable here at all, by
   * construction rather than by a check. A failure therefore never means "you typed it wrong for the other kind
   * of account" — it means this address is not a native account with that password, and the screen points at the
   * legacy form as the next thing to try.
   *
   * The session it returns is the same shape a federated sign-in produces (a contextless external session), which
   * is why {@link setActiveUser} is used rather than {@link login}'s inline storage: the landing that follows is
   * the same one too.
   */
  loginWithPassword(email: string, password: string): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/local/login`, {Email: email, Password: password},
      {withCredentials: true}).pipe(map(user => {
      this.setActiveUser(user);
      return user;
    }));
  }

  /**
   * Asks whether an address already has a native account, before the invite landing asks for a password (#583).
   *
   * **Why the screen needs it.** The redemption endpoint creates an account for an unknown address and VERIFIES
   * the password for a known one, so the same form is both a sign-up and a sign-in. Guessing which left the
   * invitee reading a button that described the opposite of what pressing it would do, and asking somebody
   * signing in to confirm a password they already have. One round trip buys an honest second step.
   *
   * **Anonymous, and gated on the invitation code rather than on a session.** "Does somebody here use this
   * address?" is answered only to a caller already holding a live invitation, who could learn the same thing by
   * redeeming it. A code that is invalid, expired, revoked or spent answers `E_GRANT_INVITE_INVALID` and reads
   * nothing about the address.
   *
   * A POST with a body rather than a GET, deliberately: the invitation code is a bearer secret, and a query
   * string is the one part of a request that reliably lands in an access log, a proxy log and the browser's own
   * history.
   */
  lookupInvitationRedemption(code: string, email: string): Observable<InvitationRedeemLookup> {
    return this.http.post<InvitationRedeemLookup>(`${this.baseUrl}/auth/local/redeem/lookup`,
      {Code: code, Email: email});
  }

  /**
   * Accepts an invitation by setting an e-mail address and a password, in one act.
   *
   * The account is created by the redemption, never before it — which is why there is no sign-up endpoint to call
   * instead. If the address already has a native account the server VERIFIES the password rather than replacing
   * it, so this doubles as "I already have an account, sign me in and apply the invitation".
   *
   * The two optional fields ride in an options object rather than as two more positional parameters. They are
   * independent, neither is the obvious "next" argument, and a second optional string behind the first is the
   * shape that eventually gets called with the values the wrong way round.
   */
  redeemInvitationWithPassword(code: string, email: string, password: string,
                               options: InvitationRedeemOptions = {}): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/local/redeem`,
      {
        Code: code, Email: email, Password: password,
        DisplayName: options.displayName ?? null,
        EmailConfirmationCode: options.emailConfirmationCode ?? null
      },
      {withCredentials: true}).pipe(map(user => {
      this.setActiveUser(user);
      return user;
    }));
  }

  /**
   * Asks for a one-time mailbox confirmation code to be mailed to an **enforced** invitation's bound address
   * (#580), so that invitation can be accepted here with an e-mail address and a password.
   *
   * Anonymous, like the redemption itself: the whole point is that the invitee has no account yet.
   *
   * **The address is sent so the server can refuse early, never so it can choose where to deliver.** The code
   * goes to the invitation's *stored* address, so this cannot be used to have a message sent anywhere of the
   * caller's choosing. Passing it only stops a code being mailed for a redemption that would then be refused for
   * naming a different address.
   *
   * The server answers 202 rather than 200 on purpose: what comes back is an acknowledgement that a message is
   * on its way plus when it stops working, not a result.
   */
  startInvitationEmailConfirmation(code: string, email: string)
    : Observable<InvitationEmailConfirmationChallenge> {
    return this.http.post<InvitationEmailConfirmationChallenge>(
      `${this.baseUrl}/auth/local/redeem/confirm/start`, {Code: code, Email: email});
  }

  /**
   * Finishes a native sign-in that owed a second factor.
   *
   * Its own endpoint rather than {@link completeTwoFactorLogin}, because the two mint different kinds of session:
   * that one produces an employee's or a management user's, this one an identity's. The server refuses a
   * challenge minted for either of the other principal kinds, so the split cannot be walked around by sending
   * one route the other's token.
   */
  completeLocalTwoFactor(challenge: string, code: string): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/local/two-factor`, {Challenge: challenge, Code: code},
      {withCredentials: true}).pipe(map(user => {
      this.setActiveUser(user);
      return user;
    }));
  }

  /** Changes the signed-in native account's own password. The current one is required; a session is not enough. */
  changeLocalPassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/local/password`,
      {CurrentPassword: currentPassword, NewPassword: newPassword}, {withCredentials: true});
  }

  /**
   * Logs the current session out. While impersonating this is a no-op (the only exit is "Stop impersonating",
   * which closes the audit session) — even the 401/403 interceptor is intentionally suppressed so a stray
   * forbidden response cannot orphan the impersonation session. Pass {@link force} to bypass for a deliberate,
   * programmatic sign-out.
   */
  logout(force: boolean = false) {
    if (this.isImpersonating && !force) {
      console.warn('Logout is disabled during impersonation. Use "Stop impersonating".');
      return;
    }
    let uid = this.userValue!.id;
    this.generalHttpService.logout(this.userValue!.jwt ?? "")
      .pipe(finalize(() => {
        localStorage.removeItem('user');
        this.userSubject.next(null);

        // remove from user selector
        this.userSelectorService.remove(uid);

        this.router.navigate(['/login']);
      })).subscribe({
      error: (err) => {
        console.error(err);
      }
    })
  }

  switch() {
    // Switching users mid-impersonation would orphan the impersonation audit session — disabled.
    if (this.isImpersonating) {
      console.warn('Switching users is disabled during impersonation. Use "Stop impersonating".');
      return;
    }
    localStorage.removeItem('user');
    this.userSubject.next(null);
    this.router.navigate(['/login'], {queryParams: {returnUrl: this.router.routerState.snapshot.url}}).then(() => {
    });
  }

  select(user: UserJwt) {
    this.cookieService.setLocalStorage(CookieType.Required, 'user', JSON.stringify(user));
    // this.userSubject.next(user);
    const returnUrl = UrlHelper.QueryParams.returnUrl || '/';
    // const returnUrl = this.route.snapshot.queryParams['returnUrl'] || '/';
    window.open(returnUrl, '_self');
  }

  isEmployee(): boolean {
    return this.userValue?.permissions.includes(Roles.Employee) ?? true;
  }

  /**
   * True for any session that renders inside its own minimal panel shell instead of the admin navbar/dashboard
   * — today {@link Roles.Employee} ({@link EmployeePanelComponent}) and {@link Roles.Operator}
   * ({@link OperatorComponent}). Both are standalone baseline roles held instead of ShardAdmin, so they must
   * never fall into the "not employee" branch that renders `app-navbar-office` (#205) — that branch assumed
   * "not Employee" meant "full admin user", which stopped being true the moment Operator was introduced.
   * Reuse this wherever chrome/offset decisions were keyed on {@link isEmployee} for that reason (the app shell,
   * the alert banner's top offset) rather than adding a second Employee-only check next to it.
   */
  isPanelUser(): boolean {
    const permissions = this.userValue?.permissions;
    if (!permissions) return true;
    return permissions.includes(Roles.Employee) || permissions.includes(Roles.Operator);
  }

  get isLoggedIn(): boolean {
    return !!this.userValue?.jwt;
  }

  /** True when the active principal has no company/shard (e.g. an Entra platform owner). */
  get isCompanyless(): boolean {
    const company: any = this.userValue?.company;
    return !company || !company.id;
  }

  /** True when the active session is impersonating another user. */
  get isImpersonating(): boolean {
    return !!this.userValue?.impersonatedBy;
  }

  /**
   * True when this session was established via a social identity, i.e. its token carries an `ext_id` claim.
   *
   * Every `/auth/context/*` endpoint reads its subject from that claim and answers
   * `E_GRANT_REDEEM_NOT_SOCIAL_SESSION` (403) without it, so this is the gate every caller checks before asking
   * where it may act — a plain password session must not fire a request that is certain to be refused.
   *
   * (Called `canSwitchAccounts` until the account switcher was removed; it never meant anything else.)
   */
  get canSelectContext(): boolean {
    return !!this.userValue?.extId;
  }

  /**
   * True when logged in AS an identity that authenticated **at a provider** — Google, Microsoft, Apple. Such a
   * session has no password stored here; it is changed where the credential lives.
   *
   * Note what this deliberately does NOT cover since #279: a **native** (e-mail + password) identity carries
   * `authSource === 'Local'`, so it answers `false` here — correctly. It is an identity principal like any
   * other, but its password IS stored here and is changed here, which is exactly the distinction this getter is
   * used to make.
   */
  get isExternalSession(): boolean {
    const source = this.userValue?.authSource;
    return !!source && source !== 'Local';
  }

  /**
   * True when the password **this session actually proved** is a native one — an e-mail + password
   * `ExternalIdentity` at provider `Local` (#279), whose credential lives in `AccountPassword` and is changed
   * at `/auth/local/password`.
   *
   * ## Why it is not simply `authSource === 'Local'`
   *
   * `JWTUser.AuthSource` **defaults** to `"Local"`, so a plain legacy username + password login carries it too.
   * Routing on `authSource` alone would send every legacy session at the native endpoint, which reads its
   * subject from the `ext_id` claim, finds none, and answers 401 — so the thing that would break is the
   * password change of every account that has *not* migrated yet. The identity claim is what actually tells the
   * two apart, which is why `extId` is required as well.
   *
   * ## Why an employee CONTEXT is deliberately included
   *
   * Entering an employee context from a native identity mints a `JWTEmployee` that carries `ExtId` along, so
   * such a session answers `true` here. That is intended, and it is not this getter's invention: it is exactly
   * the rule `SecondFactorPrincipals.Resolve` already applies server-side —
   *
   * > **The factor belongs to whatever proved the password.**
   *
   * — under which an employee context entered from a native identity resolves to *the identity's* factor,
   * because the identity's password is the one that was proved. The same reasoning decides which password the
   * profile screen may change. Sending that session at the legacy endpoint would offer to change
   * `Employee.Password` — a credential the person did not use to get in and may not even have — while the
   * `/auth/local/password` endpoint reads the very same `ext_id` claim and changes the credential they did use.
   *
   * A session that signed in through the legacy employee door carries no `ext_id` at all, so it is unaffected.
   *
   * The three session kinds are exhaustive and disjoint: {@link isExternalSession} (federated),
   * `isNativeSession` (a proved native password), and everything else (a legacy management user or employee).
   */
  get isNativeSession(): boolean {
    const user = this.userValue;
    return user?.authSource === 'Local' && !!user.extId;
  }

  /** Stores a freshly minted session as the active user (used by impersonation / context entry). */
  private setActiveUser(user: UserJwt) {
    // Critical: persist so the post-reload bootstrap picks up the new session.
    this.cookieService.setLocalStorage(CookieType.Required, 'user', JSON.stringify(user));
    // Best-effort live updates — never let a subscriber or the history list derail an otherwise
    // successful (HTTP 200) impersonation/switch by surfacing as an error on the observable.
    try {
      this.userSubject.next(user);
    } catch (e) {
      console.error('user subject update failed', e);
    }
    // Don't persist transient/identity sessions to the account history (user_history): impersonation sessions,
    // and external-identity principals (e.g. an Entra platform owner) — the latter would otherwise linger in
    // the selector after switching into a local account and logging out. Only real local logins / switched
    // local accounts are remembered.
    const isExternal = !!user.authSource && user.authSource !== 'Local';
    if (!user.impersonatedBy && !isExternal) {
      try {
        this.userSelectorService.add(user);
      } catch (e) {
        console.error('Failed to add user to selector history', e);
      }
    }
  }

  // ----- External (social) sign-in -----
  // Provider-specific OAuth init lives under /auth/{provider}/* (e.g. /auth/google/login, /auth/microsoft/login);
  // the handoff exchange and the dev mock-login are provider-neutral under /auth/social/*.

  /**
   * Redirects the browser to the DIRECT Apple sign-in (#279) — `/auth/apple/login`, straight against
   * `https://appleid.apple.com`, no Entra broker in between.
   *
   * **This 404s until `CHEQME_APPLE_*` is configured** (Services ID, team id, and the once-downloadable `.p8`).
   * That is the documented off switch every provider shares, and it is preferred here to the alternative of
   * quietly brokering through Entra: a working-looking Apple button that is secretly not the direct integration
   * is how this went unnoticed the first time.
   */
  appleLogin(returnUrl: string) {
    window.open(`${this.baseUrl}/auth/apple/login?returnUrl=${encodeURIComponent(returnUrl)}`, '_self');
  }

  /**
   * Redirects the browser to the DIRECT Google sign-in (#279) — `/auth/google/login`, straight against
   * `https://accounts.google.com`, no Entra broker in between. A Google identity carries no groups and can
   * therefore never hold a platform role.
   */
  googleLogin(returnUrl: string) {
    window.open(`${this.baseUrl}/auth/google/login?returnUrl=${encodeURIComponent(returnUrl)}`, '_self');
  }

  /**
   * Redirects the browser to the ONE Microsoft sign-in — `/auth/microsoft/login`, against the app's own
   * `/common` app registration (work, school and personal accounts). There is no separate platform door any
   * more (the Entra broker is deleted, #279): the backend inspects the token's `tid` and grants `PlatformOwner`
   * when it matches `CHEQME_MICROSOFT_PLATFORM_TENANT_ID`. The same button can therefore come back either
   * role-carrying or role-less, depending on who signed in, not on which route they used.
   */
  microsoftLogin(returnUrl: string) {
    window.open(`${this.baseUrl}/auth/microsoft/login?returnUrl=${encodeURIComponent(returnUrl)}`, '_self');
  }

  /**
   * Whether the dev mock-login is available on this backend (Development + CHEQME_DEV_AUTH). Returns false in any
   * production deployment, so the login UI can decide whether to surface the developer-login option.
   */
  devLoginAvailable(): Observable<boolean> {
    return this.http.get<{ enabled: boolean }>(`${this.baseUrl}/auth/social/dev-login/available`)
      .pipe(map(r => r.enabled), catchError(() => of(false)));
  }

  /**
   * DEV-ONLY: signs in as a dummy external identity for the given email, reusing the shared social handoff — the
   * backend mints the session and redirects to the signing-in page, exactly like a real provider sign-in.
   */
  devLogin(email: string) {
    const returnUrl = `${location.origin}/${AppRoutes.AuthenticationSigningIn}`;
    window.open(`${this.baseUrl}/auth/social/dev-login?email=${encodeURIComponent(email)}` +
      `&returnUrl=${encodeURIComponent(returnUrl)}`, '_self');
  }

  /**
   * The `/auth/{provider}/*` segment a flow should use.
   *
   * Every flow — login, link, claim, redeem — exists under all three providers with an identical shape, so this
   * is the ONE place that decides which door a given provider goes through.
   *
   * **`provider` is required, not optional (#279).** It used to default an absent provider to `entra` — the
   * broker's platform door — which is how three call sites (the employee link, the invitation redeem, and the
   * external-session link) kept quietly resolving to a route the backend has since deleted, without a single
   * caller having actually asked for Entra. A parameter that can be silently omitted is an invitation to omit
   * it; requiring it closes that off for good. Apple's routes still answer **404** until `CHEQME_APPLE_*` is
   * configured — that stays the intended behaviour: a provider that is not set up should look not-set-up.
   */
  private static endpointFor(provider: LoginProvider): string {
    return provider;
  }

  /**
   * Links an external account to the current session. link/start is authenticated, so it must be called as an
   * XHR (which carries the bearer) — it returns the provider URL, and we then navigate the browser to it.
   */
  linkMicrosoft(returnUrl: string, provider: LoginProvider) {
    this.http.get<{ url: string }>(
      `${this.baseUrl}/auth/${AuthenticationService.endpointFor(provider)}/link/start` +
      `?returnUrl=${encodeURIComponent(returnUrl)}`,
      {withCredentials: true}).subscribe({
      next: (r) => window.open(r.url, '_self'),
      error: (e) => console.error('External link start failed', e)
    });
  }

  /**
   * Claims the CURRENT legacy login for a Microsoft account (#279). Same shape as linkMicrosoft — authenticated,
   * so it goes out as an XHR and the caller navigates to the URL it returns — but a different server flow: the
   * callback also migrates the roles this login may hand over and redeems any invitation bound to the account's
   * address.
   *
   * Pass `as: 'employee'` when the session is an employee rather than a management user; the server then reads
   * the employee and company ids from the token.
   *
   * Transitional — removed with the legacy authorization system at #264.
   */
  claimForMicrosoftAccount(returnUrl: string, as: 'employee' | undefined,
                           provider: LoginProvider): Observable<{ url: string }> {
    const suffix = as ? `&as=${as}` : '';
    return this.http.get<{ url: string }>(
      `${this.baseUrl}/auth/${AuthenticationService.endpointFor(provider)}/claim/start` +
      `?returnUrl=${encodeURIComponent(returnUrl)}${suffix}`,
      {withCredentials: true});
  }

  /**
   * Claims this legacy login for an e-mail + password account — the fourth door, beside the three above (#279).
   *
   * Three things differ from `claimForMicrosoftAccount`, and all three follow from there being no third party:
   *
   * - it POSTs and *finishes*, rather than handing back a URL to navigate to. There is no provider to visit, so
   *   there is no round trip, no `?claimed=` parameter to come back with, and no page reload;
   * - it passes no `as=employee`. The server reads whether this is an employee or a management session from the
   *   token — the request never leaves the session, so it does not have to be told, and cannot be told wrongly;
   * - it can fail with something specific to say. Map the code with `describeNativeAccountError`, exactly as the
   *   invitation landing does for the same refusals: an unusable address, a weak password, or a wrong password
   *   against an account that already exists at that address.
   *
   * It answers with what was carried across rather than with a session. The caller is already signed in on the
   * legacy login being migrated and is neither signed out nor swapped by claiming — the new account is first
   * used at the next sign-in, on the e-mail + password form.
   *
   * Transitional — removed with the legacy authorization system at #264.
   */
  claimWithPassword(email: string, password: string, displayName?: string): Observable<LegacyClaimResult> {
    return this.http.post<LegacyClaimResult>(`${this.baseUrl}/auth/local/claim`,
      {Email: email, Password: password, DisplayName: displayName ?? null},
      {withCredentials: true});
  }

  /**
   * Sends the browser to the invitation redeem flow. A top-level navigation rather than an XHR: the invitee has
   * no session yet — since #279 the account is created BY redeeming — so there is no bearer to carry.
   *
   * Every external sign-in forces the provider's account chooser now (the backend always sends
   * `prompt=select_account`), so there is nothing to opt into here any more.
   */
  redeemInvitation(code: string, returnUrl: string, provider: LoginProvider) {
    window.open(`${this.baseUrl}/auth/${AuthenticationService.endpointFor(provider)}/redeem/start` +
      `?code=${encodeURIComponent(code)}&returnUrl=${encodeURIComponent(returnUrl)}`, '_self');
  }

  /**
   * Exchanges the one-time external sign-in handoff code for the minted session and activates it. Provider-neutral
   * (Entra, dev-login, later Google/Apple/Facebook all converge on the same /auth/social/complete exchange).
   */
  completeSignIn(handoff: string): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/social/complete?handoff=${encodeURIComponent(handoff)}`,
      {}, {withCredentials: true}).pipe(map(user => {
      this.setActiveUser(user);
      return user;
    }));
  }

  // ----- Impersonation -----

  /** Starts impersonating a user; stashes the owner session and activates the impersonation token. */
  startImpersonation(userId: string, reason?: string): Observable<UserJwt> {
    const origin = this.userValue;
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/impersonation/start`, {UserId: userId, Reason: reason},
      {withCredentials: true}).pipe(map(user => {
      // NB: CookieService traps the global localStorage.setItem (throws) — writes MUST go through it.
      if (origin) this.cookieService.setLocalStorage(CookieType.Required, 'impersonation_origin',
        JSON.stringify(origin));
      this.setActiveUser(user);
      return user;
    }));
  }

  /** Ends impersonation, revokes the impersonation token and restores the owner session. */
  stopImpersonation(): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/impersonation/stop`, {}, {withCredentials: true})
      .pipe(map(() => {
        const originJson = localStorage.getItem('impersonation_origin');
        if (originJson) {
          this.setActiveUser(JSON.parse(originJson));
          localStorage.removeItem('impersonation_origin');
        }
      }));
  }

  // ----- Context selection (#279) -----
  //
  // This is the ONE way a session reaches a company/shard. The account switcher's `/auth/switch` pair — which
  // minted a session from a bracket link instead of from a grant — is gone: it did not narrow roles to the
  // selected view, did not check the identity's `Deleted` state, treated any `IsPrincipal` identity as a
  // platform principal, and wrote no audit entry. `POST /auth/context/enter` does all four.
  //
  // Both this and impersonation mint a NEW session token and rotate the CM-Secret cookie server-side, so both
  // must replace the stored session with what the response returned — and the failure mode when one of them
  // forgets is not a visible error but a 401 on the *next* request, which `ErrorInterceptor` turns into a
  // logout. Keeping them in one file, calling one `setActiveUser`, is what stops the two drifting apart.

  /**
   * What this account may enter (`GET /auth/context/available`).
   *
   * Only meaningful for a session established through an external identity — the backend reads the subject from
   * the token's `ext_id` claim and answers `E_GRANT_REDEEM_NOT_SOCIAL_SESSION` (403) for a plain password
   * session. Callers should gate on {@link canSelectContext} rather than firing a request that is certain to
   * be refused.
   */
  availableContexts(): Observable<ContextOptions> {
    return this.http.get<ContextOptions>(`${this.baseUrl}/auth/context/available`, {withCredentials: true});
  }

  /**
   * Enters one context (`POST /auth/context/enter`) and makes the returned session the active one.
   *
   * Three things this call gets right, each of which has broken a session before:
   * - **`withCredentials: true`** — the CM-Secret cookie both authenticates the rotation and is what the
   *   returned JWT is encrypted with. Without it the server cannot mint, and the browser would not store the
   *   rotated cookie it sends back.
   * - **The secret is never in the body.** The controller reads it from the cookie; a caller able to supply an
   *   arbitrary one could not decrypt what it received anyway.
   * - **The returned token replaces the stored session** (via {@link setActiveUser}). Entering rotates the
   *   secret server-side, so the *previous* session's encrypted bearer stops decrypting the moment this
   *   succeeds. Keeping the old one means every subsequent request 401s and the error interceptor signs the
   *   user out — a failure that surfaces far from its cause and reads like a broken backend.
   *
   * The ids in the body are a convenience, not an authorization input: the service recomputes what this account
   * may enter and refuses (`E_CONTEXT_NOT_GRANTED`) anything outside it.
   */
  enterContext(context: SelectableContext): Observable<UserJwt> {
    const body: EnterContextRequest = {
      CompanyId: context.companyId ?? '',
      ShardId: context.shardId ?? null,
      View: context.view,
      // Part of the entry's identity, not a convenience: one account can bracket two employees of the same
      // company, and dropping it here would let the backend's match resolve to whichever it listed first.
      EmployeeId: context.employeeId ?? null
    };
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/context/enter`, body, {withCredentials: true})
      .pipe(map(user => {
        this.setActiveUser(user);
        return user;
      }));
  }

  /**
   * Enters a whole company in the management shell — the shape a payroll/company-wide grant confers.
   *
   * Exists so a caller that has *just* been granted a company (an invitation redeem) can go there without first
   * asking `/auth/context/available` for a list it already knows the answer to. The ids are still not an
   * authorization input: `Enter` recomputes what this account may enter and refuses anything outside it, so a
   * wrong company id is a refusal, not a way in.
   */
  enterCompany(companyId: string): Observable<UserJwt> {
    return this.enterContext({
      kind: SelectableContextKind.Company,
      view: ContextView.Management,
      companyId,
      roles: []
    });
  }

  /**
   * Recovers the platform/base session after the backend sweep purges a time-boxed platform-owner self-grant
   * (`POST /auth/context/recover`, #279 follow-up to the self-grant panel/slider).
   *
   * On expiry the sweep deletes the company-scoped session row underneath the token this browser is still
   * holding, so the very next request this session makes 401s — and read naively that looks exactly like a
   * dead session. It is not: the platform/base identity behind it is still good, only that one time-boxed grant
   * expired. `ErrorInterceptor` calls this BEFORE reaching for `logout()` on such a 401, and only for a session
   * that could plausibly have one to recover (see its recover branch).
   *
   * The endpoint mints a fresh, contextless platform/base session for the identity behind the purged one —
   * reading the subject off the CM-Secret cookie + the still-present (if now-401ing) bearer, never a request
   * body — and answers the SAME `UserJwt` shape {@link enterContext} does, so this reuses that shape and the
   * same full-replace {@link setActiveUser} rather than the merge-based {@link applyReissuedSession}: there is
   * no tenant left to keep here, the whole point is landing back on a contextless session, exactly like a fresh
   * context entry. A refusal (`E_CONTEXT_NOT_GRANTED`, 401/403) means there is genuinely nothing left to
   * recover, and the caller falls back to an ordinary logout.
   */
  recoverBaseSession(): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/context/recover`, {}, {withCredentials: true})
      .pipe(map(user => {
        this.setActiveUser(user);
        return user;
      }));
  }

  /**
   * Attempts {@link recoverBaseSession} and reacts to its outcome — success lands back on the chooser with the
   * "access expired" notice (never a `logout()`), failure (no grant left to recover — `E_CONTEXT_NOT_GRANTED`)
   * falls back to the ordinary logout.
   *
   * The ONE shared landing-or-logout used by BOTH triggers a time-boxed context can end at: `ErrorInterceptor`
   * calling it reactively off a 401 (the guaranteed fallback — a proactive timer that fails to fire, e.g. a
   * backgrounded tab throttling `setTimeout`, is still caught the next time a request goes out), and
   * `ContextExpiryTimerService` calling it proactively at `contextExpiresAt` (#279 follow-up hardening: land
   * BEFORE the next request 401s, plus a heads-up ~60s ahead). Living in one place is what keeps the two from
   * drifting apart — a fix applied to only one of two copies is exactly how the reactive recovery went unnoticed
   * for as long as it did.
   *
   * Joins the SHARED in-flight attempt rather than starting a new one (moved here, verbatim, from
   * `ErrorInterceptor.recoverOrLogout`/`startRecovery`): several callers can invoke this within the same tick —
   * several shell-bootstrap requests 401ing at once, or the proactive timer firing a moment before a request
   * that was already in flight also 401s — and firing one recovery call per caller would mint several sessions
   * and race their `setActiveUser` writes against each other. This is also what makes the timer and the
   * interceptor racing each other safe: whichever calls first starts the one real HTTP call, the other joins it,
   * and both land on the SAME outcome. `pick: 1` forces the chooser to show its tiles rather than silently
   * auto-selecting/auto-landing — see `ChooseContextComponent`.
   */
  recoverToChooserOrLogout(): void {
    const recovery = AuthenticationService.recoveryInFlight ?? this.startRecovery();
    recovery.subscribe({
      // A FULL document load, not a router navigation: the app shell (top navbar + bottom switcher) is chosen
      // from the session at bootstrap, and recovery has just swapped the tenant session for the platform one.
      // A SPA navigation would leave the old company's chrome on screen (stale navbar, stale switcher). Mirrors
      // ChooseContextComponent.land(), which full-reloads for exactly this reason.
      next: () => window.open(
        `/${AppRoutes.AuthenticationChooseContext}?pick=1&notice=${ContextRecoveryNotice.AccessExpired}`, '_self'),
      error: () => this.logout()
    });
  }

  /**
   * Starts the one real HTTP call behind {@link recoverToChooserOrLogout} and stashes it so concurrent callers
   * join it instead of starting their own — `shareReplay(1)` multicasts the single underlying
   * `recoverBaseSession()` subscription to every joiner, which matters here because a plain cached Observable
   * from `HttpClient` is COLD: re-subscribing to it without `shareReplay` would fire a second real request per
   * joiner, which is exactly the race this exists to prevent.
   *
   * `finalize` clears the static field once the attempt settles (either way), the same one-shot-per-attempt
   * shape as `SigningInComponent.processedHandoff`, except this one resets itself so a LATER, independent
   * expiry can recover again rather than being permanently latched out.
   */
  private startRecovery(): Observable<UserJwt> {
    const recovery = this.recoverBaseSession().pipe(
      shareReplay(1),
      finalize(() => {
        AuthenticationService.recoveryInFlight = null;
      })
    );
    AuthenticationService.recoveryInFlight = recovery;
    return recovery;
  }

  /** The shared in-flight recovery attempt, or null when none is running. See {@link startRecovery}. */
  private static recoveryInFlight: Observable<UserJwt> | null = null;

  // ----- Authenticator management (self-service: current user) -----

  getAuthenticators(): Observable<UserAuthenticators> {
    return this.http.get<UserAuthenticators>(`${this.baseUrl}/auth/account/authenticators`,
      {withCredentials: true});
  }

  removeAuthenticator(identityId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/auth/account/authenticators/${identityId}`,
      {withCredentials: true});
  }

  setPasswordLogin(enabled: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/password-login`, {Enabled: enabled},
      {withCredentials: true});
  }

  // ----- Authenticator management (admin: platform owner, any user) -----

  listAllIdentities(): Observable<ExternalIdentityInfo[]> {
    return this.http.get<ExternalIdentityInfo[]>(`${this.baseUrl}/auth/account/admin/external-identities`,
      {withCredentials: true});
  }

  getUserAuthenticators(userId: string): Observable<UserAuthenticators> {
    return this.http.get<UserAuthenticators>(`${this.baseUrl}/auth/account/admin/${userId}/authenticators`,
      {withCredentials: true});
  }

  linkUserIdentity(userId: string, identityId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/admin/${userId}/authenticators`,
      {IdentityId: identityId}, {withCredentials: true});
  }

  removeUserAuthenticator(userId: string, identityId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/auth/account/admin/${userId}/authenticators/${identityId}`,
      {withCredentials: true});
  }

  setUserPasswordLogin(userId: string, enabled: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/admin/${userId}/password-login`, {Enabled: enabled},
      {withCredentials: true});
  }

  // ----- external-identity session: linked local accounts -----

  /** Local accounts linked to the current external identity (only valid in an external session). */
  getLinkedAccounts(): Observable<BracketedAccount[]> {
    return this.http.get<BracketedAccount[]>(`${this.baseUrl}/auth/account/identity/accounts`,
      {withCredentials: true});
  }

  /**
   * Unlinks a management **user** from the current external identity.
   *
   * User-shaped, and the endpoint behind it reads `User.GetById`. An employee id resolves to nothing there and
   * answers 404 — which is what the profile page produced for every employee entry, because it routed all of
   * them here. Employees go through {@link unlinkEmployeeAccount}.
   */
  unlinkAccount(userId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/auth/account/identity/accounts/${userId}`,
      {withCredentials: true});
  }

  /**
   * Unlinks a bracketed **employee** from the current external identity.
   *
   * Both ids are needed: an employee lives in a per-company database, so the employee id alone does not
   * identify a row. The backend still only removes a link that hangs off *this* identity, so neither id is an
   * authorization input — naming another company's employee finds nothing and changes nothing.
   */
  unlinkEmployeeAccount(companyId: string, employeeId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/auth/account/identity/accounts/employee/`
      + `${encodeURIComponent(companyId)}/${encodeURIComponent(employeeId)}`,
      {withCredentials: true});
  }

  // ----- Employee authenticator management (self-service: current employee session) -----

  /** Links an external account to the current EMPLOYEE session (authenticated XHR returns the provider URL). */
  linkMicrosoftAsEmployee(returnUrl: string, provider: LoginProvider) {
    this.http.get<{ url: string }>(
      `${this.baseUrl}/auth/${AuthenticationService.endpointFor(provider)}/link/start` +
      `?as=employee&returnUrl=${encodeURIComponent(returnUrl)}`,
      {withCredentials: true}).subscribe({
      next: (r) => window.open(r.url, '_self'),
      error: (e) => console.error('Employee link start failed', e)
    });
  }

  getEmployeeAuthenticators(): Observable<UserAuthenticators> {
    return this.http.get<UserAuthenticators>(`${this.baseUrl}/auth/account/employee/authenticators`,
      {withCredentials: true});
  }

  removeEmployeeAuthenticator(identityId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/auth/account/employee/authenticators/${identityId}`,
      {withCredentials: true});
  }

  setEmployeePasswordLogin(enabled: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/employee/password-login`, {Enabled: enabled},
      {withCredentials: true});
  }

  // ----- Employee authenticator management (admin: company-scoped, by employee id) -----

  getEmployeeAuthenticatorsAdmin(employeeId: string): Observable<UserAuthenticators> {
    return this.http.get<UserAuthenticators>(
      `${this.baseUrl}/auth/account/admin/employees/${employeeId}/authenticators`, {withCredentials: true});
  }

  linkEmployeeIdentity(employeeId: string, identityId: string): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/admin/employees/${employeeId}/authenticators`,
      {IdentityId: identityId}, {withCredentials: true});
  }

  removeEmployeeAuthenticatorAdmin(employeeId: string, identityId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/auth/account/admin/employees/${employeeId}/authenticators/${identityId}`,
      {withCredentials: true});
  }

  setEmployeePasswordLoginAdmin(employeeId: string, enabled: boolean): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/auth/account/admin/employees/${employeeId}/password-login`,
      {Enabled: enabled}, {withCredentials: true});
  }

  get contractType(): ContractType {
    return parseInt(this.userValue?.metadata[MetadataType[MetadataType.ContractType]] ?? "0");
  }

  set contractType(value: ContractType) {
    if (!this.userValue) return;
    this.userValue.metadata[MetadataType[MetadataType.ContractType]] = value.toString();
  }

  // ----- Required-forms gate (employee sessions) -----

  /** The session's required-forms verdict ("ok" | "review" | "restricted"). Defaults to "ok" when absent. */
  get formsGate(): string {
    return this.userValue?.formsGate ?? FormsGate.Ok;
  }

  /** True when the session is restricted to form operations until a required form is completed. */
  get isFormsRestricted(): boolean {
    return this.formsGate === FormsGate.Restricted;
  }

  /** True when a completed submission is flagged for review (full access, but warned + time-boxed). */
  get isFormsReviewDue(): boolean {
    return this.formsGate === FormsGate.Review;
  }

  /** True when the session holds the additive {@link Roles.Auditor} privilege (may read the audit trail). */
  get isAuditor(): boolean {
    return this.userValue?.permissions?.includes(Roles.Auditor) ?? false;
  }

  /**
   * True when **this session** carries {@link Roles.PlatformOwner} — not merely when the account could.
   *
   * The distinction is the whole value of the getter. Entering a tenant narrows the session to that context's
   * roles (`ContextViewMap.RolesForView`), which drops `PlatformOwner` deliberately, so an owner working inside
   * a company genuinely does not hold it and every platform-only endpoint refuses them. The two picker
   * affordances that need platform authority — self-activation and taking over an account on a tile — gate on
   * this, which is why they appear on the platform shell and are absent once a tenant has been entered. That is
   * the truth rather than a limitation: the way back is the `Plattform` tile.
   */
  get isPlatformOwner(): boolean {
    return this.userValue?.permissions?.includes(Roles.PlatformOwner) ?? false;
  }

  /** The review deadline when {@link isFormsReviewDue}, otherwise null. */
  get formsReviewDue(): Date | null {
    const due = this.userValue?.reviewDue;
    return due ? new Date(due) : null;
  }

  // ----- Account-security (two-factor) gate — #282 -----

  /**
   * The session's account-security verdict ("ok" | "restricted"). Defaults to "ok" when absent.
   *
   * The default is load-bearing, not tidiness: every session already stored in a browser predates this claim,
   * and reading absence as anything but "ok" would restrict all of them on the deploy that introduced it.
   */
  get accountSecurity(): string {
    return this.userValue?.accountSecurity ?? AccountSecurity.Ok;
  }

  /**
   * True when the session has proven a password but not the second factor its company requires, and may
   * therefore reach only the two-factor remediation screen.
   *
   * The twin of {@link isFormsRestricted}, and read in the same three places (the guard, the error interceptor,
   * the remediation screen). It differs in one respect worth knowing before using it: it is **not** confined to
   * employee sessions, so callers must not guard it behind a `Roles.Employee` check the way the forms gate is.
   */
  get isAccountSecurityRestricted(): boolean {
    return this.accountSecurity === AccountSecurity.Restricted;
  }

  // ----- Required contact-channel gate (employee sessions) — #558 -----

  /**
   * The session's contact-channel verdict ("ok" | "review" | "restricted"). Defaults to "ok" when absent.
   *
   * The default carries the same weight as its two siblings above: every session already stored in a browser
   * predates this claim, and reading absence as anything but "ok" would restrict all of them on the deploy
   * that introduced it.
   */
  get contactGate(): string {
    return this.userValue?.contactGate ?? ContactGate.Ok;
  }

  /**
   * True when the session may only reach the contact-linking screen until a required channel is linked (or an
   * overdue re-verification is done).
   *
   * The exact twin of {@link isFormsRestricted}, and read in the same places — the guard, the error
   * interceptor, `GateRefreshService` and the remediation screen — through the one shared rule in
   * `gate-lockout-route.ts`. Like the forms gate and unlike the account-security one it is employee-only.
   */
  get isContactRestricted(): boolean {
    return this.contactGate === ContactGate.Restricted;
  }

  /**
   * True when a contact-linking task is open but **not yet blocking** (a backfill grace, or a re-verification
   * inside its window): full access, warned and time-boxed. Drives the panel's warning strip.
   */
  get isContactReviewDue(): boolean {
    return this.contactGate === ContactGate.Review;
  }

  /** The contact deadline when {@link isContactReviewDue}, otherwise null. */
  get contactReviewDue(): Date | null {
    const due = this.userValue?.contactReviewDue;
    return due ? new Date(due) : null;
  }

  // ----- Employment gate (employee sessions) — #864 -----

  /**
   * The session's employment verdict ("ok" | "restricted"). Defaults to "ok" when absent, for the same reason as
   * its three siblings: every session already stored in a browser predates the claim.
   */
  get employmentGate(): string {
    return this.userValue?.employmentGate ?? EmploymentGate.Ok;
  }

  /**
   * True when the employee's employment has not started yet. The session is held to the forms, contact and
   * two-factor screens until {@link employmentStartsAt}, and lifted by `GateRefreshService` after that.
   */
  get isEmploymentPending(): boolean {
    return this.employmentGate === EmploymentGate.Restricted;
  }

  /** When {@link isEmploymentPending}: the instant the employment starts. Otherwise null. */
  get employmentStartsAt(): Date | null {
    if (!this.isEmploymentPending) return null;
    const at = this.userValue?.employmentStartsAt;
    return at ? new Date(at) : null;
  }

  /**
   * True when **any** gate wants the panel's warning strip up — a forms review, or a contact one.
   *
   * Exists because the strip is deliberately a **single 40 px row** whatever it is warning about: the panel's
   * own content offset and `AlertBannerComponent.TopOffsetClass` both restate that chrome height as one
   * `top-24`, so two independent "is my strip up" checks would eventually disagree and push the global alert
   * banner under a strip nobody accounted for. One question, one answer, two readers.
   */
  get isGateReviewDue(): boolean {
    return this.isFormsReviewDue || this.isContactReviewDue;
  }

  /**
   * Completes a sign-in that was refused with `E_AUTH_TWO_FACTOR_REQUIRED`: hands the challenge back with a TOTP
   * or recovery code and, on success, establishes the session **exactly** as {@link login} does.
   *
   * It lives here rather than on `SecondFactorHttpService` precisely because of that last clause. The response
   * is a session, and a session has to be written to `localStorage` through `CookieService` (the global
   * `localStorage.setItem` is consent-trapped and throws), pushed onto `userSubject`, and registered with
   * `UserSelectorService` — three steps that `login()` already performs and that a second implementation
   * elsewhere would eventually stop performing identically. Sign-in is one behaviour with two request shapes,
   * not two behaviours.
   *
   * @param challenge the opaque challenge from the refused first step.
   * @param code      a TOTP code, or one of the account's recovery codes.
   */
  completeTwoFactorLogin(challenge: string, code: string): Observable<UserJwt> {
    return this.http.post<UserJwt>(`${this.baseUrl}/auth/User/TwoFactor`, {
      "Challenge": challenge,
      "Code": code
    }, {
      withCredentials: true
    }).pipe(map(user => {
      this.cookieService.setLocalStorage(CookieType.Required, 'user', JSON.stringify(user));
      this.userSubject.next(user);

      this.userSelectorService.add(user);

      return user;
    }));
  }

  /**
   * Applies a freshly re-issued employee token (from {@code /auth/forms-gate/reissue}) to the active session
   * without a re-login: merges the new token body over the current user so company/shard and history are kept,
   * while {@code jwt}/{@code expiration}/{@code permissions}/{@code formsGate}/{@code reviewDue}/
   * {@code accountSecurity}/{@code contactGate}/{@code contactReviewDue}/{@code employmentGate}/
   * {@code employmentStartsAt} are refreshed.
   *
   * Shared verbatim with the two-factor gate's `/auth/account/two-factor/reissue` (#282), the contact gate's
   * `/auth/contact-gate/reissue` and the live `/auth/gates/refresh` (#558) — deliberately NOT copied into a
   * second "apply" for any of them. The merge semantics (keep the tenant, take the new claims) are the
   * whole contract, and two implementations of it would drift on exactly the field one of them forgot.
   * The CM-Secret cookie is unchanged (the server re-uses it), so the encrypted bearer keeps decrypting.
   */
  applyReissuedSession(token: Partial<UserJwt>): UserJwt {
    const merged = {...(this.userValue as UserJwt), ...token} as UserJwt;
    // NB: CookieService traps the global localStorage.setItem (throws) — writes MUST go through it.
    this.cookieService.setLocalStorage(CookieType.Required, 'user', JSON.stringify(merged));
    this.userSubject.next(merged);
    return merged;
  }
}

/** The employment gate verdict carried on the session (#864, mirrors the server's employment_gate values). */
export const EmploymentGate = {
  Ok: 'ok',
  Restricted: 'restricted'
} as const;

/** The required-forms gate verdict carried on the session (mirrors the server's forms_gate claim values). */
export const FormsGate = {
  Ok: 'ok',
  Review: 'review',
  Restricted: 'restricted'
} as const;
